import moment from 'moment';
import { Inject } from '@odinjs/odin';

import Controller, { OnBeforeCancel, OnBlur, OnChange, OnFocus, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { AutocompleteAPI, CheckboxAPI, ContextualBarAPI, FieldAPI, FormAPI, InputAPI, Metadata, ModalProps, ModalService, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { AbortableCloseEvent, AbortableSubmitEvent, ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { ModalType, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { NursingInterventionsFieldsMap } from './nursing-interventions-fields-map';
import { ConsistEmarRecordsRequest } from '../nursing-interventions-start/services/start-nursing-interventions-service';
import { MedicineAndSolutionsParams } from '../../medicine-and-solutions/medicine-and-solutions-params';
import BaseController from '../../../../BaseController';
import { CdScalePain, HeightUnit, WeightUnit } from '../../../../vital-sign/vital-sign-create-and-edit/vital-sign-create-and-edit-controller';
import { ExceptionalAttributes } from '../../../../vital-sign/vital-sign-create-and-edit/vital-sign-create-and-edit-service';
import { FragmentNames, NursingInterventionsEmarRecordsAliases } from '../../../../../codegen';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { BooleanUtils } from '../../../../../utils/boolean-utils';
import ValidateTypeScale from '../../../../vital-sign/services/validate-type-scale';
import ValidateAttributesService, { ValidateAttributesRequest } from './services/validate-attributes-service';
import { ClinicalParameters } from './clinical-parameters';
import { AutocompleteValue, FormFields, InvasiveMechanicalType, ModalityAndInvasiveMechanicalType, NursingInterventionSchedule, NursingInterventionsFields, RespirationType } from './nursing-interventions-records-type';
import GetAttributesService, { GetAttributeModality } from './services/get-attributes-service';
import ExecuteNursingInterventionsService from '../nursing-interventions-start/services/start-nursing-interventions-service';
import MedicationService from '../../../../../controllers/adep/services/medication-service';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { SavedRecordsContext } from './services/saved-records-context';

const NR_SEQ_TIPO_ATENDIMENTO_PERDA_GANHO = 'NR_SEQ_TIPO__ATENDIMENTO_PERDA_GANHO';

@Controller({ component: NursingInterventionsEmarRecordsAliases.NURSING_INTERVENTIONS_EMAR_RECORDS_LAYOUT, fragment: FragmentNames.NURSING_INTERVENTIONS_EMAR_RECORDS })
export default class NursingInterventionsRecordController extends BaseController {

  @Inject(NursingInterventionsEmarRecordsAliases.PANEL_SUCCESS_LOADER)
  private panelLoaderApi!: PanelAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DYNAMIC_DATA_TIME_FIELD)
  public fieldDateTimeApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FORM)
  private formApi!: FormAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PASITION_PA_FIELD)
  public fieldPositionPaApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MEMBER_PA_FIELD)
  public fieldMemberPaApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAS_FIELD)
  public fieldPasApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAD_FIELD)
  public fieldPadApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAM_FIELD)
  public fieldPamApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEART_RATE_FIELD)
  public fieldHeartRateApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RESPIRATORY_FREQUENCY_FIELD)
  public fieldRespiratoryFrequencyApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TEMPERATURE_FIELD)
  public fieldTemperatureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PLACE_FIELD)
  public fieldPlaceApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CD_PAIN_SCALE_FIELD)
  public fieldCdPainScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAIN_SCALE_FIELD)
  public fieldPainScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAIN_SCALE_INPUT)
  private inputPainScaleApi!: InputAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RESULT_FIELD)
  public fieldResultApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TOPOGRAPHY_FIELD)
  public fieldTopographyApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.IE_SIDE_FIELD)
  public fieldIeSIdeApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.KIND_OF_PAIN_FIELD)
  public fieldKindOfPainApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAIN_CONDITION_FIELD)
  public fieldPainConditionApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DURATION_FIELD)
  public fieldDurationApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TIME_COURSE_FIELD)
  public fieldTimeCourseApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FREQUENCY_FIELD)
  public fieldFrequencyApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.IRRADIATION_TOPOGRAPHY_FIELD)
  public fieldIrradiationTopographyApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OUTCAST_SIDE_FIELD)
  public fieldOutcastSideApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAIN_COMFORT_LEVEL_FIELD)
  public fieldPainComfortLevelApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INEPT_REASON_PAIN_FIELD)
  public fieldIneptReasonPainApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CONDUCT_RECORD_FIELD)
  public fieldConductRecordApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SATURATION_O2_FIELD)
  public fieldSaturationO2Api!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SATURATION_CONDITION_FIELD)
  public fieldSaturationConditionApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FC_OXI_FIELD)
  public fieldFcOxiApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SATURATION_MEMBER_FIELD)
  public fieldSaturationMemberApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PVC_MMHF_FIELD)
  public fieldPvcMmhfApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PPA_MMHG_FIELD)
  public fieldPpaMmhgApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PIC_MMHG_FIELD)
  public fieldPicMmhgApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PIC_CATHETER_TEMPERATURE_FIELD)
  public fieldPicCatheterTemperatureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PPC_MMHG_FIELD)
  public fieldPpcMmhgApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DATE_OF_WEIGHT_FIELD)
  public fieldDateOfWeightApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.WEIGHT_UNIT_MEASURE_FIELD)
  public fieldWeightUnitMeasureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEIGHT_UNIT_MEASURE_FIELD)
  public fieldHeightUnitMeasureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEIGHT_ORIGIN_FIELD)
  public fieldHeightOriginApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SC_FIELD)
  public fieldScApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INCUBATOR_HUMIDITY_FIELD)
  public fieldIncubatorHumidityApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INCUBATOR_TEMPERATURE_FIELD)
  public fieldIncubatorTemperatureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CRIB_TEMPERATURE_FIELD)
  public fieldCribTemperatureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.BTT_FIELD)
  public fieldBttApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.BTF_FIELD)
  public fieldBtfApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RADIANCE_FIELD)
  public fieldRadianceApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RIGHT_PULLEY_SIZE_FIELD)
  public fieldRightPulleySizeApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.LEFT_PULLEY_SIZE_FIELD)
  public fieldLeftPulleySizeApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RIGHT_EYE_LIGHT_REACTION_FIELD)
  public fieldRightEyeLightReactionApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.LEFT_EYE_LIGHT_REACTION_FIELD)
  public fieldLeftEyeLightReactionApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.LEVEL_OF_CONSCIOUSNESS_FIELD)
  public fieldLevelOfConsciousnessApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_FIELD)
  public fieldObstetricRecordsApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF2_FIELD)
  public fieldObstetricRecordsBcf2Api!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF3_FIELD)
  public fieldObstetricRecordsBcf3Api!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEIGHT_CENTIMETERS_FIELD)
  public fieldHeightCentimetersApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.BLOOD_GLUCOSE_CONTROL_FIELD)
  public fieldBloodGlucoseControlApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INAUDIBLE_BP_FIELD)
  public InaudibleBpFieldAPI!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INAUDIBLE_BP_CHECKBOX)
  public InaudibleBpCheckboxAPI!: CheckboxAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.APAP_FIELD)
  public apapFieldAPI!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.APAP_CHECKBOX)
  public apapCheckboxAPI!: CheckboxAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.KANGAROO_MOTHER_CARE_METHOD_FIELD)
  public kangarooMotherCareMethodFieldAPI!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.KANGAROO_MOTHER_CARE_METHOD_CHECKBOX)
  public kangarooMotherCareMethodCheckboxAPI!: CheckboxAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE)
  public autocompleteHeightUnitMeasureApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE)
  public autocompleteWeightUnitMeasureApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.APAP_HOUR_AUTOCOMPLETE)
  public autocompleteApapHourApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.APAP_HOUR_FIELD)
  public apapHourFieldApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MAEC_BIS_FIELD)
  public fieldMaecBisApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PIA_MMHG_FIELD)
  public fieldPiaMmhgApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RIKER_SCALE_AUTOCOMPLETE)
  public autocompleteRickScaleApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RIKER_SCALE_FIELD)
  public fieldRickScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RASS_SCALE_AUTOCOMPLETE)
  public autocompleteRassScaleApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RASS_SCALE_FIELD)
  public fieldRassScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RAMSAY_SCALE_AUTOCOMPLETE)
  public autocompleteRamsayScaleApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RAMSAY_SCALE_FIELD)
  public fieldRamsayScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.POSS_SCALE_AUTOCOMPLETE)
  public autocompletePossScaleApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.POSS_SCALE_FIELD)
  public fieldPossScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.COMFORT_B_SCALE_FIELD)
  public fieldComfortBScaleApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.O2_ADDITIONAL_FIELD)
  public fieldO2AdditionalApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.KG_WEIGHT_FIELD)
  public fieldKgWeightApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.G_WEIGHT_FIELD)
  public fieldGweightApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HEIGHT_METERS_FIELD)
  public fieldHeightMetersApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.ORIGIN_WIDTH_FIELD)
  public fieldOriginWidthApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OBSERVATION_INTAKE_OUTPUT_FIELD)
  public fieldObservationIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SIDE_INTAKE_OUTPUT_FIELD)
  public fieldSideIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SIDE_INTAKE_OUTPUT_AUTOCOMPLETE)
  public autocompleteSideIntakeOutputApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.NEWBORN_INTAKE_OUTPUT_FIELD)
  public fieldNewbornIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.NEWBORN_INTAKE_OUTPUT_CHECKBOX)
  public checkboxNewbornIntakeOutputApi!: CheckboxAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HOUR_APAP_INTAKE_OUTPUT_FIELD)
  public fieldHourApapIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HOUR_APAP_INTAKE_OUTPUT_AUTOCOMPLETE)
  public autocompleteHourApapInatekOutputApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DEVICE_INTAKE_OUTPUT_FIELD)
  public fieldDeviceIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DEVICE_INTAKE_OUTPUT_AUTOCOMPLETE)
  public autocompleteDeviceIntakeOutputApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TYPE_INTAKE_OUTPUT_FIELD)
  public fieldTypeIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TYPE_INTAKE_OUTPUT_AUTOCOMPLETE)
  public autocompleteTypeIntakeOutputApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TOPOGRAPHY_INTAKE_OUTPUT_FIELD)
  public fieldTopographyIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TOPOGRAPHY_INTAKE_OUTPUT_AUTOCOMPLETE)
  public autocompleteTopographyIntakeOutputApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.OCCURRENCES_INTAKE_OUTPUT_FIELD)
  public fieldOccurrencesIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.WEIGHT_INTAKE_OUTPUT_FIELD)
  public fieldWeightIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.VOLUME_INTAKE_OUTPUT_FIELD)
  public fieldVolumeIntakeOutputApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FC_OXI_MONITORING_FIELD)
  public fieldFcOxiMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.SATURATION_O2_MONITORING_FIELD)
  public fieldSaturationMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FI_O2_MONITORING_FIELD)
  public fieldFiO2MonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.AMPLITUDE_MONITORING_FIELD)
  public fieldAmplitudeMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FREQ_VENT_MONITORING_FIELD)
  public fieldFreqVentMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.FREQ_PROG_MONITORING_FIELD)
  public fieldFreqProgMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PROGRAMMED_TIDAL_MONITORING_FIELD)
  public fieldProgrammedTidalMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INSPIRATORY_TIDAL_MONITORING_FIELD)
  public fieldInspiratoryTidalMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.EXPIRATORY_TIDAL_MONITORING_FIELD)
  public fieldExpiratoryTidalMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CYCLE_FINAL_MONITORING_FIELD)
  public fieldCycleFinalMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PARTIAL_MINUTE_MONITORING_FIELD)
  public fieldPartialMinuteMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PC_MONITORING_FIELD)
  public fieldPcMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.AUTO_PEEP_MONITORING_FIELD)
  public fieldAutoPeepMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CO2_INSPIRED_MONITORING_FIELD)
  public fieldCo2InspiredMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DYNAMIC_COMPLIANCE_MONITORING_FIELD)
  public fieldDynamicComplianceMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.EXPIRATION_TIME_MONITORING_FIELD)
  public fieldExpirationTimeMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PAO2_FIO2_MONITORING_FIELD)
  public fieldPao2Fio2MonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CUFF_PRESSURE_MONITORING_FIELD)
  public fieldCuffPressureMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.VITAL_CAPACITY_MONITORING_FIELD)
  public fieldVitalCapacityMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.PEP_MONITORING_FIELD)
  public fieldPepMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.TOBIN_INDEX_MONITORING_FIELD)
  public fieldTobinIndexMonitoringApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RESPIRATION_TYPE_AUTOCOMPLETE)
  public autocompleteRespirationTypeApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.RESPIRATION_TYPE_FIELD)
  public fieldRespirationTypeApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HUMIDIFIER_FILTER_AUTOCOMPLETE)
  public autocompleteHumidifierFilterApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HUMIDIFIER_FILTER_FIELD)
  public fieldHumidifierFilterApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.BREATHING_PATTERN_AUTOCOMPLETE)
  public autocompleteBreathingPatternApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.BREATHING_PATTERN_FIELD)
  public fieldBreathingPatternApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MODALITY_AUTOCOMPLETE)
  public autocompleteModalityApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MODALITY_FIELD)
  public fieldModalityApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MANEUVER_AUTOCOMPLETE)
  public autocompleteManeuverApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.MANEUVER_FIELD)
  public fieldManeuverApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DEVICE_INTERFACE_AUTOCOMPLETE)
  public autocompleteDeviceInterfaceApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DEVICE_INTERFACE_FIELD)
  public fieldDeviceInterfaceApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INVASIVE_MECHANICAL_VENTILATION_DEVICE_AUTOCOMPLETE)
  public autocompleteInvasiveVentilationApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INVASIVE_MECHANICAL_VENTILATION_DEVICE_FIELD)
  public fieldInvasiveVentilationApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.DEVICE_NUMBER_FIELD)
  public fieldDeviceNumberApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.LIPLINE_FIELD)
  public fieldLiplineApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.LIPLINE_INPUT)
  public inputLiplineApi!: InputAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INDICATION_AUTOCOMPLETE)
  public autocompleteIndicationApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.INDICATION_FIELD)
  public fieldIndicationApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.EQUIPMENT_AUTOCOMPLETE)
  public autocompleteEquipmentnApi!: AutocompleteAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.EQUIPMENT_FIELD)
  public fieldEquipmentnApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.HUMIDIFIER_TEMPERATURE_FIELD)
  public fieldHumidifierTemperatureApi!: FieldAPI;

  @Inject(NursingInterventionsEmarRecordsAliases.CONTEXTUAL_BAR_EMAR_RECORDS)
  private contextualBarEmarRecordsApi!: ContextualBarAPI;

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.NURSING_INTERVENTIONS_EMAR_RECORDS_LAYOUT })
  handleMountLayout(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({
      encounterId,
      patientId,
    });

    const emarRecordTitle = MessageService.translate('text.emar-records')
    this.contextualBarEmarRecordsApi.setTitle(`${emarRecordTitle}: ${this.context.get('currentIntervention').description}`)
  }

  @OnMount(NursingInterventionsEmarRecordsAliases.FORM)
  handleMountForm({ target }: EventData<FormAPI>): void {
    this.panelLoaderApi.setVisible(true);
    target.setDisabled(true)
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const params = {
      encounterId: Number(encounterId),
      nursingInterventionsId: this.context.get('currentIntervention').itemCode,
      patientId: Number(patientId)
    }

    GetAttributesService
      .execute(params)
      .then(({ component, values }) => {
        this.validateWhichFieldsAreVisible(component)
        this.context.set({ responseBodyComponent: component, responseBodyValues: values });
      }).finally(() => {
        target.setDisabled(false)
        this.panelLoaderApi.setVisible(false);
      })
  }

  private setVisibleFieldAPI(customApi: AutocompleteAPI | CheckboxAPI | TextAreaAPI | null, fieldApi: FieldAPI, fieldEntry: string, input: InputAPI | null): void {
    const fieldMap = this.context.get('fieldMap') as Record<string, NursingInterventionsFields>;
    if (!fieldMap) {
      return
    }

    const field = fieldMap[fieldEntry]?.metadata as Metadata;
    if (!field) {
      return
    }

    const fieldLabel = field.label as string;
    if (customApi instanceof AutocompleteAPI) {
      customApi?.setData(field.options as Record<string, Value>[]);
    }

    const savedValues = SavedRecordsContext.get()
    if (savedValues && savedValues[fieldApi.getEntry()]) {
      fieldApi.setValue(savedValues[fieldApi.getEntry()])
    }

    const fieldFormatterType = input?.getFormatters()?.value?.name
    fieldApi.setLabel(fieldLabel);
    fieldApi.setValidators([
      { name: field?.required === true ? 'required' : '' },
      { name: customApi || fieldFormatterType === 'date' || field.fieldType === 'TEXT_AREA' ? '' : 'numeric' }
    ]);
    customApi?.setEnabled(true);
    customApi?.setReadOnly(false);
    input?.setReadOnly(false);
  }

  private validateWhichFieldsAreVisible(metadata: Metadata): void {
    const fieldMap = NursingInterventionsFieldsMap.makeData(this);

    metadata?.children?.forEach((fieldSet) => {
      fieldSet?.children?.forEach((field) => {
        const fieldEntry = field.entry as string;
        const fm = fieldMap[fieldEntry];

        if (fm) {
          fieldMap[fieldEntry] = { ...fieldMap[fieldEntry], metadata: field };
        }
      });
    });

    this.context.set({ fieldMap: fieldMap });

    Object.keys(fieldMap)
      .filter(key => fieldMap[key].metadata != null)
      .forEach(key => fieldMap[key].field.setVisible(true));
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.O2_ADDITIONAL_INPUT })
  handleMountQtO2Suplementar({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldO2AdditionalApi, this.fieldO2AdditionalApi.getEntry(), target);
  }

  @OnChange({ target: NursingInterventionsEmarRecordsAliases.DYNAMIC_DATA_TIME_INPUT })
  onDataTimeInputChange(evt: ChangeEvent<InputAPI>): void {
    const newDateState = moment(String(evt.value));
    this.context.set({dateState: newDateState});
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.APAP_HOUR_AUTOCOMPLETE })
  handleMountApapHour({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.apapHourFieldApi, this.apapHourFieldApi.getEntry(), null);
    this.apapHourFieldApi.setVisible(true);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  handleMountIeUnidMedAltura({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldHeightUnitMeasureApi, this.fieldHeightUnitMeasureApi.getEntry(), null);
    if (this.fieldHeightMetersApi.isVisible() && this.fieldHeightCentimetersApi.isVisible()) {
      this.autocompleteHeightUnitMeasureApi.setValue({label: HeightUnit.M, value: HeightUnit.M})
      this.fieldHeightMetersApi.setVisible(true);
      this.fieldHeightCentimetersApi.setVisible(false);
    } else if (this.fieldHeightMetersApi.isVisible() && !this.fieldHeightCentimetersApi.isVisible()) {
      this.autocompleteHeightUnitMeasureApi.setValue({label: HeightUnit.M, value: HeightUnit.M})
      this.fieldHeightMetersApi.setVisible(true);
      this.fieldHeightCentimetersApi.setVisible(false);
    } else if (!this.fieldHeightMetersApi.isVisible() && this.fieldHeightCentimetersApi.isVisible()) {
      this.autocompleteHeightUnitMeasureApi.setValue({label: HeightUnit.CM, value: HeightUnit.CM})
      this.fieldHeightMetersApi.setVisible(false);
      this.fieldHeightCentimetersApi.setVisible(true);
    } else {
      this.fieldHeightMetersApi.setVisible(false);
      this.fieldHeightCentimetersApi.setVisible(false);
      this.fieldHeightUnitMeasureApi.setVisible(false)
    }
  }

  @OnChange({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onChangeIeUnidMedAltura(event: ChangeEvent<FieldAPI>): void {
    this.handleAutocompleteHeightChange(event, this.fieldHeightCentimetersApi, this.fieldHeightMetersApi)
  }

  private handleAutocompleteHeightChange(event: ChangeEvent<FieldAPI>, fieldCentimetersApi: FieldAPI, fieldMetersApi: FieldAPI): void {
    const inputValue = event.prettyValue;
    const hasOldValue = JSON.stringify(event.oldValue);
    const centimetersSelected = this.unitSelected(inputValue, HeightUnit.CM)
    const metersSelected = this.unitSelected(inputValue, HeightUnit.M)
    const metersFilled = Number(fieldMetersApi.getValue() ?? 0);
    const centimetersFilled = Number(fieldCentimetersApi.getValue() ?? 0);

    if(metersSelected && centimetersFilled && hasOldValue) {
      fieldMetersApi.setValue((centimetersFilled / 100).toFixed(2));
      fieldCentimetersApi.setValue('');
    }

    if(centimetersSelected && metersFilled && hasOldValue) {
      fieldCentimetersApi.setValue(metersFilled * 100)
      fieldMetersApi.setValue('')
    }

    if(inputValue == '') {
      fieldCentimetersApi.setValue('');
      fieldMetersApi.setValue('')
    }
    fieldMetersApi.setVisible(metersSelected);
    fieldCentimetersApi.setVisible(centimetersSelected);
  }

  private unitSelected(inputValue: string | Record<string, string> | undefined, measureUnit: string): boolean {
    return inputValue === measureUnit && inputValue != null;
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  handleMountIeUnidMedPeso({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldWeightUnitMeasureApi, this.fieldWeightUnitMeasureApi.getEntry(), null);
    if (this.fieldKgWeightApi.isVisible() && this.fieldGweightApi.isVisible()) {
      this.autocompleteWeightUnitMeasureApi.setValue({label: WeightUnit.KG, value: WeightUnit.KG})
      this.fieldKgWeightApi.setVisible(true);
      this.fieldGweightApi.setVisible(false);
    } else if (this.fieldKgWeightApi.isVisible() && !this.fieldGweightApi.isVisible()) {
      this.autocompleteWeightUnitMeasureApi.setValue({label: WeightUnit.KG, value: WeightUnit.KG})
      this.fieldKgWeightApi.setVisible(true);
      this.fieldGweightApi.setVisible(false);
    } else if (!this.fieldKgWeightApi.isVisible() && this.fieldGweightApi.isVisible()) {
      this.autocompleteWeightUnitMeasureApi.setValue({label: WeightUnit.G, value: WeightUnit.G})
      this.fieldKgWeightApi.setVisible(false);
      this.fieldGweightApi.setVisible(true);
    } else {
      this.fieldKgWeightApi.setVisible(false);
      this.fieldGweightApi.setVisible(false);
      this.fieldWeightUnitMeasureApi.setVisible(false)
    }
  }

  @OnChange({ target: NursingInterventionsEmarRecordsAliases.WEIGHT_UNIT_MEASURE_AUTOCOMPLETE })
  onChangeUnidMedPeso(event: ChangeEvent<FieldAPI>): void {
    this.handleAutocompleteWeightChange(event, this.fieldKgWeightApi, this.fieldGweightApi);
  }

  private handleAutocompleteWeightChange(event: ChangeEvent<FieldAPI>, fieldKgWeightApi: FieldAPI, fieldGweightApi: FieldAPI): void {
    const inputValue = event.prettyValue;
    const oldValue = JSON.stringify(event.oldValue)
    const isKilogram = inputValue === WeightUnit.KG && inputValue != null
    const isGram = inputValue === WeightUnit.G && inputValue != null
    const kgFieldValue = Number(fieldKgWeightApi.getValue());
    const gFieldValue = Number(fieldGweightApi.getValue());

    if (kgFieldValue && isGram && oldValue) {
      fieldGweightApi.setValue(kgFieldValue * 1000);
      this.clearField(fieldKgWeightApi)
    }

    if(gFieldValue && isKilogram && oldValue) {
      fieldKgWeightApi.setValue(gFieldValue / 1000)
      this.clearField(fieldGweightApi)
    }

    if(inputValue == '') {
      this.clearField(fieldKgWeightApi, fieldGweightApi)
    }

    fieldKgWeightApi.setVisible(isKilogram);
    fieldGweightApi.setVisible(isGram);
  }

  private clearField(kg?: FieldAPI, gram?: FieldAPI): void {
    if(kg && gram) {
      kg.setValue('')
      gram.setValue('')
    } else if (kg) {
      kg.setValue('')
    } else if (gram) {
      gram.setValue('')
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.KANGAROO_MOTHER_CARE_METHOD_CHECKBOX })
  handleMountKangarooMotherCareMethodCheckbox({ target }: EventData<CheckboxAPI>): void {
    this.setVisibleFieldAPI(target, this.kangarooMotherCareMethodFieldAPI, this.kangarooMotherCareMethodFieldAPI.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INAUDIBLE_BP_CHECKBOX })
  handleMountInaudibleBp({ target }: EventData<CheckboxAPI>): void {
    this.setVisibleFieldAPI(target, this.InaudibleBpFieldAPI, this.InaudibleBpFieldAPI.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.APAP_CHECKBOX })
  handleMountApapCheckbox({ target }: EventData<CheckboxAPI>): void {
    this.setVisibleFieldAPI(target, this.apapFieldAPI, this.apapFieldAPI.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.POSITION_PA_AUTOCOMPLETE })
  handleMountIePressao({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldPositionPaApi, this.fieldPositionPaApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.MEMBER_PA_AUTOCOMPLETE })
  handleMountIeMembro({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldMemberPaApi, this.fieldMemberPaApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PIA_MMHG_INPUT })
  handleMountPiaMmhg({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPiaMmhgApi, this.fieldPiaMmhgApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.MAEC_BIS_INPUT })
  handleMountMaecBis({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldMaecBisApi, this.fieldMaecBisApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAS_INPUT })
  handleMountQtPaSistolica({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPasApi, this.fieldPasApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAD_INPUT })
  handleMountQtPaDiastolica({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPadApi, this.fieldPadApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAM_INPUT })
  handleMountQtPam({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPamApi, this.fieldPamApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HEART_RATE_INPUT })
  handleMountQtFreqCardiaca({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldHeartRateApi, this.fieldHeartRateApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RESPIRATORY_FREQUENCY_INPUT })
  handleMountQtFreqResp({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldRespiratoryFrequencyApi, this.fieldRespiratoryFrequencyApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TEMPERATURE_INPUT })
  handleMountQtTemp({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldTemperatureApi, this.fieldTemperatureApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PLACE_AUTOCOMPLETE })
  handleMountIeSitio({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldPlaceApi, this.fieldPlaceApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CD_PAIN_SCALE_AUTOCOMPLETE })
  handleMountCdEscalaDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldCdPainScaleApi, this.fieldCdPainScaleApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OBSERVATION_INTAKE_OUTPUT_TEXT_AREA })
  handleMountDsObservacao({ target }: EventData<TextAreaAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldObservationIntakeOutputApi, this.fieldObservationIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SIDE_INTAKE_OUTPUT_AUTOCOMPLETE })
  handleMountIeLadoIntakeOutput({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldSideIntakeOutputApi, this.fieldSideIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.NEWBORN_INTAKE_OUTPUT_CHECKBOX })
  handleMountIeRecemNascido({ target }: EventData<CheckboxAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldNewbornIntakeOutputApi, this.fieldNewbornIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HOUR_APAP_INTAKE_OUTPUT_AUTOCOMPLETE })
  handleMountNrHora({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldHourApapIntakeOutputApi, this.fieldHourApapIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DEVICE_INTAKE_OUTPUT_AUTOCOMPLETE })
  handleMountNrSeqDispositivo({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldDeviceIntakeOutputApi, this.fieldDeviceIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TYPE_INTAKE_OUTPUT_AUTOCOMPLETE })
  handleMountNrSeqTipo({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldTypeIntakeOutputApi, this.fieldTypeIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TOPOGRAPHY_INTAKE_OUTPUT_AUTOCOMPLETE })
  handleMountNrSeqTopografia({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldTopographyIntakeOutputApi, this.fieldTopographyIntakeOutputApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OCCURRENCES_INTAKE_OUTPUT_INPUT })
  handleMountQtOcorrencia({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldOccurrencesIntakeOutputApi, this.fieldOccurrencesIntakeOutputApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.WEIGHT_INTAKE_OUTPUT_INPUT })
  handleMountQtPeso({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldWeightIntakeOutputApi, this.fieldWeightIntakeOutputApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.VOLUME_INTAKE_OUTPUT_INPUT })
  handleMountQtVolume({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldVolumeIntakeOutputApi, this.fieldVolumeIntakeOutputApi.getEntry(), target);
  }

  @OnFocus({ target: NursingInterventionsEmarRecordsAliases.PAIN_SCALE_INPUT })
  onFocusPainScaleField(): void {
    const cdPainScaleField = this.fieldCdPainScaleApi.getValue() as CdScalePain;
    ValidateTypeScale.valdiate(cdPainScaleField?.value, this.inputPainScaleApi);
  }

  @OnChange({ target: NursingInterventionsEmarRecordsAliases.CD_PAIN_SCALE_FIELD })
  onChangePainScaleFields(event: ChangeEvent<FieldAPI>): void {
    if(event.value) {
      ValidateTypeScale.valdiate(String(event.value), this.inputPainScaleApi);
    } else {
      this.inputPainScaleApi.setValue('');
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAIN_SCALE_INPUT })
  handleMountQtEscalaDor({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPainScaleApi, this.fieldPainScaleApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RESULT_INPUT })
  handleMountNrSeqResultDor({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldResultApi, this.fieldResultApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.COMFORT_B_SCALE_INPUT })
  handleMountConforBScaleInput({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldComfortBScaleApi, this.fieldComfortBScaleApi.getEntry(), target);
  }

  @OnFocus({ target: NursingInterventionsEmarRecordsAliases.COMFORT_B_SCALE_INPUT })
  handleFocusComfortBScaleInput({ target }: EventData<InputAPI>): void {
    const dialogModal: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      contextData: {
        setValue: (comfortBValue: number): void => {
          target.setValue(comfortBValue);
        }
      },
      focusOnOpen: false,
      fragment: FragmentNames.COMFORT_B_SCALE,
      id: FragmentNames.COMFORT_B_SCALE,
      type: ModalType.FULLSCREEN,
      uuid: generateUUID(),
    };

    ModalService.openDefault(dialogModal);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TOPOGRAPHY_AUTOCOMPLETE })
  handleMountNrSeqTopografiaDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldTopographyApi, this.fieldTopographyApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.IE_SIDE_AUTOCOMPLETE })
  handleMountIeLado({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldIeSIdeApi, this.fieldIeSIdeApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.KIND_OF_PAIN_AUTOCOMPLETE })
  handleMountIeTipoDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldKindOfPainApi, this.fieldKindOfPainApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAIN_CONDITION_AUTOCOMPLETE })
  handleMountNrSeqCondicaoDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldPainConditionApi, this.fieldPainConditionApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DURATION_INPUT })
  handleMountQtPeriodo({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldDurationApi, this.fieldDurationApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TIME_COURSE_AUTOCOMPLETE })
  handleMountIePeriodo({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldTimeCourseApi, this.fieldTimeCourseApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FREQUENCY_AUTOCOMPLETE })
  handleMountIeFrequenciaDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldFrequencyApi, this.fieldFrequencyApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.IRRADIATION_TOPOGRAPHY_AUTOCOMPLETE })
  handleMountNrSeqTopDorIrradia({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldIrradiationTopographyApi, this.fieldIrradiationTopographyApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OUTCAST_SIDE_AUTOCOMPLETE })
  handleMountIeLadoDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldOutcastSideApi, this.fieldOutcastSideApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAIN_COMFORT_LEVEL_AUTOCOMPLETE })
  handleMountIeNivelConfDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldPainComfortLevelApi, this.fieldPainComfortLevelApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INEPT_REASON_PAIN_AUTOCOMPLETE })
  handleMountNrSeqInaptoDor({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldIneptReasonPainApi, this.fieldIneptReasonPainApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CONDUCT_RECORD_AUTOCOMPLETE })
  handleMountNrSeqConduta({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldConductRecordApi, this.fieldConductRecordApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SATURATION_O2_INPUT })
  handleMountQtSaturacaoO2({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldSaturationO2Api, this.fieldSaturationO2Api.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SATURATION_CONDITION_AUTOCOMPLETE })
  handleMountEeCondSatO2({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldSaturationConditionApi, this.fieldSaturationConditionApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FC_OXI_INPUT })
  handleMountQtSpO2R({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldFcOxiApi, this.fieldFcOxiApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SATURATION_MEMBER_AUTOCOMPLETE })
  handleMountIeMembroSatO2({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldSaturationMemberApi, this.fieldSaturationMemberApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.BLOOD_GLUCOSE_CONTROL_AUTOCOMPLETE })
  handleMountIeGlicExtrapol({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldBloodGlucoseControlApi, this.fieldBloodGlucoseControlApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PVC_MMHF_INPUT })
  handleMountQtPvc({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPvcMmhfApi, this.fieldPvcMmhfApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PPA_MMHG_INPUT })
  handleMountQtPpa({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPpaMmhgApi, this.fieldPpaMmhgApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PIC_MMHG_INPUT })
  handleMountQtPressaoIntraCranio({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPicMmhgApi, this.fieldPicMmhgApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PIC_CATHETER_TEMPERATURE_INPUT })
  handleMountQtPicTemp({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPicCatheterTemperatureApi, this.fieldPicCatheterTemperatureApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PPC_MMHG_INPUT })
  handleMountQtPpc({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPpcMmhgApi, this.fieldPpcMmhgApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DATE_OF_WEIGHT_INPUT })
  handleMountDtDeltaPeso({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldDateOfWeightApi, this.fieldDateOfWeightApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.KG_WEIGHT_INPUT})
  handleMountKgWeight({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldKgWeightApi, this.fieldKgWeightApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.G_WEIGHT_INPUT})
  onMountGweight({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldGweightApi, this.fieldGweightApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_CENTIMETERS_INPUT})
  handleMountQtAlturaCentimeters({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldHeightCentimetersApi, this.fieldHeightCentimetersApi.getEntry(), target)
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_CENTIMETERS_INPUT})
  onBlurQtAlturaCentimeters({ target }: ChangeEvent<string>): void {
    const value = target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_ALTURA_CM__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldHeightCentimetersApi);
    this.fieldHeightCentimetersApi.setValue(this.getValueWithoutComma(this.fieldHeightCentimetersApi).toFixed(2))
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_METERS_INPUT})
  onMountQtAlturaMeters({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldHeightMetersApi, this.fieldHeightMetersApi.getEntry(), target)
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_METERS_INPUT})
  onBlurQtAlturaMeters({ target }: ChangeEvent<string>): void {
    const value = target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_ALTURA_M__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldHeightMetersApi);
    this.fieldHeightMetersApi.setValue(this.getValueWithoutComma(this.fieldHeightMetersApi).toFixed(2))
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HEIGHT_ORIGIN_AUTOCOMPLETE })
  handleMountIeOrigemAltura({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldHeightOriginApi, this.fieldHeightOriginApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SC_INPUT })
  handleMountQtSuperfCorporia({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldScApi, this.fieldScApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INCUBATOR_HUMIDITY_INPUT })
  handleMountQtUmidIncubadora({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldIncubatorHumidityApi, this.fieldIncubatorHumidityApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INCUBATOR_TEMPERATURE_INPUT })
  handleMountQtTempIncubadora({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldIncubatorTemperatureApi, this.fieldIncubatorTemperatureApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CRIB_TEMPERATURE_INPUT })
  handleMountQtTempBerco({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldCribTemperatureApi, this.fieldCribTemperatureApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.BTT_INPUT })
  handleMountQtBilirrubinaTronco({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldBttApi, this.fieldBttApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.BTF_INPUT })
  handleMountQtBilirrubinaFronte({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldBtfApi, this.fieldBtfApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RADIANCE_INPUT })
  handleMountQtIrradiancia({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldRadianceApi, this.fieldRadianceApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RIGHT_PULLEY_SIZE_AUTOCOMPLETE })
  handleMountQtPupilaTamanho({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRightPulleySizeApi, this.fieldRightPulleySizeApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.LEFT_PULLEY_SIZE_AUTOCOMPLETE })
  handleMountQtPupilaTamSeq({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldLeftPulleySizeApi, this.fieldLeftPulleySizeApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RIGHT_EYE_LIGHT_REACTION_AUTOCOMPLETE })
  handleMountIePupilaReacaoLuz({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRightEyeLightReactionApi, this.fieldRightEyeLightReactionApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.LEFT_EYE_LIGHT_REACTION_AUTOCOMPLETE })
  handleMountIePupilaReacaoLuzE({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldLeftEyeLightReactionApi, this.fieldLeftEyeLightReactionApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.LEVEL_OF_CONSCIOUSNESS_AUTOCOMPLETE })
  handleMountIeNivelConsciencia({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldLevelOfConsciousnessApi, this.fieldLevelOfConsciousnessApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_INPUT })
  handleMountQtBcf({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsApi, this.fieldObstetricRecordsApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF2_INPUT })
  handleMountQtBcf2({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsBcf2Api, this.fieldObstetricRecordsBcf2Api.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF3_INPUT })
  handleMountQtBcf3({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldObstetricRecordsBcf3Api, this.fieldObstetricRecordsBcf3Api.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RIKER_SCALE_AUTOCOMPLETE })
  handleMountRickScale({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRickScaleApi, this.fieldRickScaleApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RASS_SCALE_AUTOCOMPLETE })
  handleMountRassScale({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRassScaleApi, this.fieldRassScaleApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RAMSAY_SCALE_AUTOCOMPLETE })
  handleMountCookScale({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRamsayScaleApi, this.fieldRamsayScaleApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.POSS_SCALE_AUTOCOMPLETE })
  handleMountPossScale({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldPossScaleApi, this.fieldPossScaleApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.ORIGIN_WIDTH_AUTOCOMPLETE })
  handleMountOriginWidth({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldOriginWidthApi, this.fieldOriginWidthApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FC_OXI_MONITORING_INPUT })
  handleMountQtSpO2RMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldFcOxiMonitoringApi, this.fieldFcOxiMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.SATURATION_O2_MONITORING_INPUT })
  handleMountQtSaturationMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldSaturationMonitoringApi, this.fieldSaturationMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FI_O2_MONITORING_INPUT })
  handleMountFiO2Monitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldFiO2MonitoringApi, this.fieldFiO2MonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.AMPLITUDE_MONITORING_INPUT })
  handleMountAmplitudeMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldAmplitudeMonitoringApi, this.fieldAmplitudeMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FREQ_VENT_MONITORING_INPUT })
  handleMountFreqVentMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldFreqVentMonitoringApi, this.fieldFreqVentMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.FREQ_PROG_MONITORING_INPUT })
  handleMountFreqProgMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldFreqProgMonitoringApi, this.fieldFreqProgMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PROGRAMMED_TIDAL_MONITORING_INPUT })
  handleMountProgrammedTidalMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldProgrammedTidalMonitoringApi, this.fieldProgrammedTidalMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INSPIRATORY_TIDAL_MONITORING_INPUT })
  handleMountInspiratoryTidalMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldInspiratoryTidalMonitoringApi, this.fieldInspiratoryTidalMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.EXPIRATORY_TIDAL_MONITORING_INPUT })
  handleMountExpiratoryTidalMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldExpiratoryTidalMonitoringApi, this.fieldExpiratoryTidalMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CYCLE_FINAL_MONITORING_INPUT })
  handleMountCycleFinalMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldCycleFinalMonitoringApi, this.fieldCycleFinalMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PARTIAL_MINUTE_MONITORING_INPUT })
  handleMountPartialMinuteMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPartialMinuteMonitoringApi, this.fieldPartialMinuteMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PC_MONITORING_INPUT })
  handleMountPcMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPcMonitoringApi, this.fieldPcMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.AUTO_PEEP_MONITORING_INPUT })
  handleMountAutoPeepMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldAutoPeepMonitoringApi, this.fieldAutoPeepMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CO2_INSPIRED_MONITORING_INPUT })
  handleMountCo2InspiredMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldCo2InspiredMonitoringApi, this.fieldCo2InspiredMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DYNAMIC_COMPLIANCE_MONITORING_INPUT })
  handleMountDynamicComplianceMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldDynamicComplianceMonitoringApi, this.fieldDynamicComplianceMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.EXPIRATION_TIME_MONITORING_INPUT })
  handleMountExpirationTimeMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldExpirationTimeMonitoringApi, this.fieldExpirationTimeMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PAO2_FIO2_MONITORING_INPUT })
  handleMountPao2Fio2Monitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPao2Fio2MonitoringApi, this.fieldPao2Fio2MonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.CUFF_PRESSURE_MONITORING_INPUT })
  handleMountCuffPressureMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldCuffPressureMonitoringApi, this.fieldCuffPressureMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.VITAL_CAPACITY_MONITORING_INPUT })
  handleMountVitalCapacityMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldVitalCapacityMonitoringApi, this.fieldVitalCapacityMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.PEP_MONITORING_INPUT })
  handleMountPepMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldPepMonitoringApi, this.fieldPepMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.TOBIN_INDEX_MONITORING_INPUT })
  handleMountTobinIndexMonitoring({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldTobinIndexMonitoringApi, this.fieldTobinIndexMonitoringApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.RESPIRATION_TYPE_AUTOCOMPLETE })
  handleMountRespirationTypeAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldRespirationTypeApi, this.fieldRespirationTypeApi.getEntry(), null);

  }

  @OnChange(NursingInterventionsEmarRecordsAliases.RESPIRATION_TYPE_AUTOCOMPLETE)
  handleChangeRespirationType( event: ChangeEvent<string>): void {
    const nrSeqRespiratioType = event.value as unknown as AutocompleteValue;
    const respirationType = nrSeqRespiratioType === undefined ? null : nrSeqRespiratioType as unknown as AutocompleteValue;

    const params = {
      respirationType: respirationType?.value === undefined ? '' : respirationType?.value,
    }

    GetAttributeModality
      .execute(params)
      .then((res) => {
        const response = res as unknown as ModalityAndInvasiveMechanicalType[];
        const invasiveMechanical = response[0] as unknown as ModalityAndInvasiveMechanicalType[];
        const modality = response[1] as unknown as ModalityAndInvasiveMechanicalType[];
        this.setAutocompleteValues(modality,invasiveMechanical);
        this.fieldsAccordingToTypeOfRespirationType();
      }).catch((error) => {
        const errorMessage = error.body.errors[0];
        MedicationService.showToast(errorMessage, ToastLevel.ERROR);
      });
  }

  private setAutocompleteValues(modality: ModalityAndInvasiveMechanicalType[], invasiveMechanical: ModalityAndInvasiveMechanicalType[]): void { 
    const listInvasiveMechanical = this.mapToAutocompleteData(invasiveMechanical); this.autocompleteInvasiveVentilationApi.setData(listInvasiveMechanical); 
    const listModality = this.mapToAutocompleteData(modality); this.autocompleteModalityApi.setData(listModality);
  }

  private mapToAutocompleteData(items: ModalityAndInvasiveMechanicalType[]): { label: string | undefined, value: string | undefined }[] { 
    return items.map(item => ({ label: item?.ds, value: item?.cd })); 
  }

  private fieldsAccordingToTypeOfRespirationType(): void {
    const fieldRespirationType = this.fieldRespirationTypeApi.getValue() as AutocompleteValue;
    if (fieldRespirationType === undefined) {
      this.autocompleteBreathingPatternApi.setEnabled(false);
      this.autocompleteManeuverApi.setEnabled(false);
      this.autocompleteDeviceInterfaceApi.setEnabled(false);
      this.autocompleteEquipmentnApi.setEnabled(true);
    } else {
      switch (fieldRespirationType.value) {
        case RespirationType.ESPONT:
          this.autocompleteBreathingPatternApi.setEnabled(true);
          this.autocompleteDeviceInterfaceApi.setEnabled(true);
          this.autocompleteEquipmentnApi.setEnabled(false);
          break;
        case RespirationType.VMI:
          this.autocompleteManeuverApi.setEnabled(true);
          break;
        case RespirationType.VMNI:
          this.autocompleteBreathingPatternApi.setEnabled(true);
          this.autocompleteManeuverApi.setEnabled(true);
          this.autocompleteDeviceInterfaceApi.setEnabled(true);
          this.autocompleteEquipmentnApi.setEnabled(true);
          break;
        default:
          this.autocompleteBreathingPatternApi.setEnabled(true);
          this.autocompleteDeviceInterfaceApi.setEnabled(true);
          this.autocompleteEquipmentnApi.setEnabled(true);
          this.autocompleteManeuverApi.setEnabled(true);
          break;
      }
    }
  }

  private fieldsAccordingToTypeOfInvasiveMechanical(value: string): void {
    this.inputLiplineApi.setEnabled(false);
    const nrSeqInvasiveMechanical = value as unknown as AutocompleteValue;
    if (nrSeqInvasiveMechanical.value === InvasiveMechanicalType.COT) {
      this.inputLiplineApi.setEnabled(true);
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HUMIDIFIER_FILTER_AUTOCOMPLETE })
  handleMountHumidifierFilterAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldHumidifierFilterApi, this.fieldHumidifierFilterApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.BREATHING_PATTERN_AUTOCOMPLETE })
  handleMountBreathingPatternAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldBreathingPatternApi, this.fieldBreathingPatternApi.getEntry(), null);
    const respirationType = this.fieldRespirationTypeApi.getValue();
    if (respirationType === undefined) {
      this.autocompleteBreathingPatternApi.setEnabled(false);
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.MODALITY_AUTOCOMPLETE })
  handleMountModalityAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldModalityApi, this.fieldModalityApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.MANEUVER_AUTOCOMPLETE })
  handleMountManeuverAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldManeuverApi, this.fieldManeuverApi.getEntry(), null);
    const respirationType = this.fieldRespirationTypeApi.getValue();
    if (respirationType === undefined) {
      this.autocompleteManeuverApi.setEnabled(false);
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DEVICE_INTERFACE_AUTOCOMPLETE })
  handleMountDeviceInterfaceAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldDeviceInterfaceApi, this.fieldDeviceInterfaceApi.getEntry(), null);
    const respirationType = this.fieldRespirationTypeApi.getValue();
    if (respirationType === undefined) {
      this.autocompleteDeviceInterfaceApi.setEnabled(false);
    }
  }

  @OnChange({ target: NursingInterventionsEmarRecordsAliases.INVASIVE_MECHANICAL_VENTILATION_DEVICE_AUTOCOMPLETE })
  handleChangeInvasiveMechanical( event: ChangeEvent<string>): void {
    const nrSeqInvasiveMechanical = event.value;
    this.fieldsAccordingToTypeOfInvasiveMechanical(nrSeqInvasiveMechanical);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INVASIVE_MECHANICAL_VENTILATION_DEVICE_AUTOCOMPLETE })
  handleMountInvasiveVentilationAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldInvasiveVentilationApi, this.fieldInvasiveVentilationApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DEVICE_NUMBER_INPUT })
  handleMountDeviceNumberInput({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldDeviceNumberApi, this.fieldDeviceNumberApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.LIPLINE_INPUT })
  handleMountLiplineInput({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldLiplineApi, this.fieldLiplineApi.getEntry(), target);
    const invasiveMechanical = this.fieldInvasiveVentilationApi.getValue();
    if (invasiveMechanical === undefined) {
      this.inputLiplineApi.setEnabled(false);
    }
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.INDICATION_AUTOCOMPLETE })
  handleMountIndicationAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldIndicationApi, this.fieldIndicationApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.EQUIPMENT_AUTOCOMPLETE })
  handleMountEquipmentAutocomplete({ target }: EventData<AutocompleteAPI>): void {
    this.setVisibleFieldAPI(target, this.fieldEquipmentnApi, this.fieldEquipmentnApi.getEntry(), null);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.HUMIDIFIER_TEMPERATURE_INPUT })
  handleMountHumidifierTemperatureInput({ target }: EventData<InputAPI>): void {
    this.setVisibleFieldAPI(null, this.fieldHumidifierTemperatureApi, this.fieldHumidifierTemperatureApi.getEntry(), target);
  }

  @OnMount({ target: NursingInterventionsEmarRecordsAliases.DYNAMIC_DATA_TIME_INPUT })
  handleMountDateTime({ target }: EventData<InputAPI>): void {
    target.setReadOnly(false);
    const responseBodyValues = this.context.get('responseBodyValues');
    const dateState = this.context.get('dateState');

    if (responseBodyValues) {
      responseBodyValues.DT_SINAL_VITAL = dateState;
      this.formApi.setData(responseBodyValues as unknown as Record<string, Value>);
    }

    this.formApi.setData({ DT_SINAL_VITAL:  dateState });
  }

  validateInputs(message: string, messageType: string | undefined, field: FieldAPI): void {
    const validatorName = 'outOfRangerValidator';
    field.setValidators([{ name: validatorName, options: {
      message,
      status: messageType
    }}])
    this.formApi.validate()
  }

  validateFieldOnBlur(attributeId: number, attributeValue: number, fieldApi: FieldAPI): void {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const params = {
      attributeId,
      attributeValue,
      encounterId
    } as ValidateAttributesRequest

    ValidateAttributesService.execute(params).then((response) => {
      this.validateInputs(response?.message, response?.status, fieldApi);
    });
  }

  @OnBeforeCancel({ target: NursingInterventionsEmarRecordsAliases.FORM })
  handleBeforeCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }

  @OnSubmit({ target: NursingInterventionsEmarRecordsAliases.FORM })
  onBeforeSubmitForm(event: AbortableSubmitEvent): void {
    this.formApi.validate()
    this.formApi.setDisabled(true)
    this.formApi.setLoading(true)
    const painScaleCode = this.fieldCdPainScaleApi.isVisible() && this.fieldCdPainScaleApi.getValue();
    const painScaleValue = this.fieldPainScaleApi.isVisible() && this.fieldPainScaleApi.getValue() === undefined;

    if (painScaleCode && painScaleValue) {
      this.inputPainScaleApi.setFocus(true);
      return
    }

    const formData = this.formApi.getRecord();
    const dateTime = moment(formData.DT_SINAL_VITAL as string).format(ISO_FULL_DATE_FORMAT);
    const formInfos = this.createFormInfos(event);
    const records = formInfos.map((field) => ({
      attributeId: field.attributeId,
      attributeName: field.attributeName,
      value: String(field.value),
    }))

    const body: ConsistEmarRecordsRequest = {
      encounterId: String(MedicineAndSolutionsParams.params.encounterId),
      records,
      scheduleId: Number(MedicineAndSolutionsParams.params.hourSequence),
      type: this.getType(),
      vitalSingDate: dateTime,
    }

    const intervention = this.context.get('currentIntervention')
    Promise.all([this.executeNursingIntevention(intervention), this.submitFormData(body)])
      .then(() => {
        const savedRecords = SavedRecordsContext.get()
        SavedRecordsContext.set({...savedRecords, ...formData})
      })
      .catch(() => {
        this.formApi.setLoading(false)
        this.formApi.setDisabled(false)
      })
      .finally(() => {
        ModalService.close(`${FragmentNames.NURSING_INTERVENTIONS_EMAR_RECORDS}-${intervention?.id}`);
      })
  }

  private executeNursingIntevention(intervention?: NursingInterventionSchedule): Promise<void> {
    const requestBody: Record<string, Value> = {
      alterationDate: this.context.get('alterationDate'),
      encounterNumber: MedicineAndSolutionsParams.params.encounterId,
      eventDate: MedicineAndSolutionsParams.params.startId,
      hourSequence: MedicineAndSolutionsParams.params.hourSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: intervention?.itemCode,
      itemSequence: intervention?.id,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justifyDescription: this.context.get('justifyDescription') || '',
      observationDescription: this.context.get('observationDescription') || '',
      prescriptionNumber: intervention?.prescriptionNumber,
      scheduleSequence: intervention?.scheduleSequence,
    } as Record<string, Value>;

    return ExecuteNursingInterventionsService
      .executeWithoutMessages(requestBody)
  }

  private getType(): number {
    const fieldMap = this.context.get('fieldMap') as Record<string, NursingInterventionsFields>;
    const field = fieldMap[NR_SEQ_TIPO_ATENDIMENTO_PERDA_GANHO]?.field?.getValue() as Record<string, Value>;
    return Number(field?.value ?? 0) ?? 0;
  }

  private submitFormData(body: ConsistEmarRecordsRequest): Promise<boolean> {
    return ExecuteNursingInterventionsService
      .consistEmarRecords(body)
      .then(() => {
        return true
      }).catch(() => {
        return false
      })
  }

  private createFormInfos(event: AbortableSubmitEvent): FormFields[] {
    const keys = Object.keys(event.record);
    const values = Object.values(event.record);

    const fieldMap = this.context.get('fieldMap') as Record<string, NursingInterventionsFields>;
    if (!fieldMap) {
      return []
    }

    return keys.map((key, index) => {
      const attributeName = [key][0].split('__')[0]
      const field = fieldMap[[key][0]]?.metadata as Metadata;
      if (typeof values[index] === 'object') {
        return {
          attributeId: field.id,
          attributeName,
          value: values[index].value
        };
      }

      let value = typeof values[index] === 'boolean' ? BooleanUtils.isPositiveValue(values[index]) : values[index]
      if (field?.formatters?.value?.name === 'date') {
        value = moment(values[index]).utc().format('DD/MM/YYYY hh:mm:ss')
      }

      return  {
        attributeId: field.id,
        attributeName,
        value
      };
    }).filter(this.removeExceptionalAttributes);
  }

  private removeExceptionalAttributes(item: FormFields): boolean {
    return !Object.keys(ExceptionalAttributes).includes(item.attributeName);
  }

  private getValueWithoutComma(fieldApi: FieldAPI): number {
    const result = Number(String(fieldApi.getValue()).replace(',' , '.'))
    return Number(result.toFixed(2))
  }

  private getAttributeId(attributeName: ClinicalParameters): number {
    const fieldMap = this.context.get('fieldMap') as Record<string, NursingInterventionsFields>;
    const attribute = ClinicalParameters[attributeName]
    const field = fieldMap[attribute]?.metadata as Metadata;
    return Number(field.id)
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.TEMPERATURE_INPUT })
  handleBlurTemperatureInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    const formattedValue = value.replace(/,/g, '.');
    event.target.setValue(formattedValue);
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_TEMP__ATENDIMENTO_SINAL_VITAL), Number(formattedValue), this.fieldTemperatureApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PAS_INPUT })
  handleBlurPasInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PA_SISTOLICA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPasApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PAD_INPUT })
  handleBlurPadInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PA_DIASTOLICA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPadApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.RESPIRATORY_FREQUENCY_INPUT })
  handleBlurRespiratoryFrequencyInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_FREQ_RESP__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldRespiratoryFrequencyApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.SATURATION_O2_INPUT })
  handleBlurSaturationO2Input(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_SATURACAO_O2__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldSaturationO2Api);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PAM_INPUT })
  handleBlurPamInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PAM__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPamApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.HEART_RATE_INPUT })
  handleBlurHeartRateInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_FREQ_CARDIACA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldHeartRateApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PAIN_SCALE_INPUT })
  handleBlurPainScaleInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_ESCALA_DOR__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPainScaleApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_INPUT })
  handleBlurObstetricRecordsInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_BCF__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldObstetricRecordsApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.KG_WEIGHT_INPUT })
  handleBlurKgWeightInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PESO__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldKgWeightApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.SC_INPUT })
  handleBlurSCInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_SUPERF_CORPORIA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldScApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PVC_MMHF_INPUT })
  handleBlurPvcMmhfInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PVC__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPvcMmhfApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PIC_MMHG_INPUT })
  handleBlurPicMmhgInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PRESSAO_INTRA_CRANIO__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPicMmhgApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.INCUBATOR_TEMPERATURE_INPUT })
  handleBlurIncubatorTempHumidityInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_TEMP_INCUBADORA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldIncubatorTemperatureApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF2_INPUT })
  handleBlurObstetricRecordsBcf2Input(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_BCF_2__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldObstetricRecordsBcf2Api);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.OBSTETRIC_RECORDS_BCF3_INPUT })
  handleBlurObstetricRecordsBcf3Input(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_BCF_3__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldObstetricRecordsBcf3Api);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.DURATION_INPUT })
  handleBlurDurationInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PERIODO__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldDurationApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.O2_ADDITIONAL_INPUT })
  handleBlurO2AdditionalInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_O2_SUPLEMENTAR__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldO2AdditionalApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.FC_OXI_INPUT })
  handleBlurFcOxiInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_SPO2R__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldFcOxiApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PPA_MMHG_INPUT })
  handleBluroPpaMmhgInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PPA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPpaMmhgApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PIC_CATHETER_TEMPERATURE_INPUT })
  handleBlurPicCatheterTemperatureInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PIC_TEMP__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPicCatheterTemperatureApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PPC_MMHG_INPUT })
  handleBlurPpcMmhgInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PPC__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPpcMmhgApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.MAEC_BIS_INPUT })
  handleBlurMaecBisInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_MAEC__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldMaecBisApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.G_WEIGHT_INPUT })
  handleBlurGWeightInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PESO_ATUAL__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldGweightApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.INCUBATOR_HUMIDITY_INPUT })
  handleBlurIncubatorHumidityInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_UMID_INCUBADORA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldIncubatorHumidityApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.CRIB_TEMPERATURE_INPUT })
  handleBlurCribTemperatureInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_TEMP_BERCO__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldCribTemperatureApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.BTT_INPUT })
  handleBlurBttInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_BILIRRUBINA_TRONCO__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldBttApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.BTF_INPUT })
  handleBlurBtfInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_BILIRRUBINA_FRONTE__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldBtfApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.RADIANCE_INPUT })
  handleBlurRadianceInput(event: ChangeEvent<string>): void {
    const value = event.target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_IRRADIANCIA__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldRadianceApi);
  }

  @OnBlur({ target: NursingInterventionsEmarRecordsAliases.PIA_MMHG_INPUT })
  handleBlurPiaMmhg({ target }: EventData<InputAPI>): void {
    const value = target.getValue() as unknown as string;
    this.validateFieldOnBlur(this.getAttributeId(ClinicalParameters.QT_PRESSAO_INTRA_ABD__ATENDIMENTO_SINAL_VITAL), Number(value), this.fieldPiaMmhgApi);
  }
}
